import { useMemo, ReactElement } from 'react';
import { Team, Venue, KnockoutMatch } from '../types/tournament';
import { usePredictions } from '../context/PredictionContext';
import { getTeamById, getVenueById } from '../hooks/useBracketData';
import { calculateBracketLayout, CARD_WIDTH, CARD_HEIGHT, getRoundLabel } from '../utils/bracketLayout';
import { MatchCard } from './MatchCard';

interface KnockoutBracketProps {
  matches: KnockoutMatch[];
  teams: Team[];
  venues: Venue[];
}

export function KnockoutBracket({ matches, teams, venues }: KnockoutBracketProps) {
  const { resolveTeamSource } = usePredictions();

  const layout = useMemo(() => calculateBracketLayout(matches), [matches]);

  // Resolve team IDs based on predictions
  const resolvedMatches = useMemo(() => {
    return layout.matches.map(pos => {
      const team1Id = resolveTeamSource(pos.match.team1Source, matches);
      const team2Id = resolveTeamSource(pos.match.team2Source, matches);

      return {
        ...pos,
        team1: team1Id ? getTeamById(teams, team1Id) : undefined,
        team2: team2Id ? getTeamById(teams, team2Id) : undefined,
        venue: getVenueById(venues, pos.match.venueId),
      };
    });
  }, [layout.matches, matches, teams, venues, resolveTeamSource]);

  return (
    <div
      className="relative"
      style={{
        width: layout.width + 100,
        height: layout.height + 100,
        minWidth: layout.width + 100,
        minHeight: layout.height + 100,
      }}
    >
      {/* Connection lines (SVG) */}
      <svg
        className="absolute top-0 left-0 pointer-events-none"
        width={layout.width + 100}
        height={layout.height + 100}
        style={{ overflow: 'visible' }}
      >
        <defs>
          <marker
            id="arrowhead-right"
            markerWidth="8"
            markerHeight="8"
            refX="6"
            refY="4"
            orient="0"
          >
            <polygon points="0,0 8,4 0,8" fill="#94a3b8" />
          </marker>
          <marker
            id="arrowhead-left"
            markerWidth="8"
            markerHeight="8"
            refX="2"
            refY="4"
            orient="180"
          >
            <polygon points="0,0 8,4 0,8" fill="#94a3b8" />
          </marker>
        </defs>
        <ConnectorLines positions={resolvedMatches} />
      </svg>

      {/* Match cards */}
      {resolvedMatches.map(({ x, y, match, team1, team2, venue }) => (
        <div
          key={match.id}
          className="absolute"
          style={{
            left: x + 50,
            top: y + 50,
          }}
        >
          <MatchCard
            match={match}
            team1={team1}
            team2={team2}
            venue={venue}
          />
        </div>
      ))}

      {/* Round labels */}
      <RoundLabels layout={layout} />
    </div>
  );
}

interface MatchWithPosition {
  x: number;
  y: number;
  match: KnockoutMatch;
  team1: Team | undefined;
  team2: Team | undefined;
  venue: Venue | undefined;
}

interface ConnectorLinesProps {
  positions: MatchWithPosition[];
}

function ConnectorLines({ positions }: ConnectorLinesProps) {
  const lines: ReactElement[] = [];
  const OFFSET = 50; // Canvas padding offset
  const LINE_COLOR = '#94a3b8'; // slate-400 (matches arrowhead color)

  positions.forEach(pos => {
    const { match, x, y } = pos;

    // Find feeder matches (matches whose winner feeds into this match)
    const feeders = positions.filter(other => {
      return (
        match.team1Source === `W${other.match.matchNumber}` ||
        match.team2Source === `W${other.match.matchNumber}` ||
        match.team1Source === `L${other.match.matchNumber}` ||
        match.team2Source === `L${other.match.matchNumber}`
      );
    });

    feeders.forEach(feeder => {
      const isTeam1 = match.team1Source === `W${feeder.match.matchNumber}` ||
                      match.team1Source === `L${feeder.match.matchNumber}`;

      // Determine if left or right side of bracket
      const isLeftSide = feeder.x < x;

      // Start point: center-right of feeder card (left side) or center-left (right side)
      const startX = isLeftSide
        ? feeder.x + OFFSET + CARD_WIDTH
        : feeder.x + OFFSET;
      const startY = feeder.y + OFFSET + CARD_HEIGHT / 2;

      // End point: center-left of target card (left side) or center-right (right side)
      const endX = isLeftSide
        ? x + OFFSET
        : x + OFFSET + CARD_WIDTH;
      // Connect to top or bottom third of target card
      const endY = y + OFFSET + (isTeam1 ? CARD_HEIGHT * 0.3 : CARD_HEIGHT * 0.7);

      // Midpoint X for the vertical segment
      const midX = (startX + endX) / 2;

      // Choose arrowhead based on direction
      const arrowhead = isLeftSide ? 'url(#arrowhead-right)' : 'url(#arrowhead-left)';

      // Draw path: horizontal -> vertical -> horizontal with arrowhead
      lines.push(
        <path
          key={`${feeder.match.id}-to-${match.id}`}
          d={`M ${startX} ${startY} H ${midX} V ${endY} H ${endX}`}
          fill="none"
          stroke={LINE_COLOR}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          markerEnd={arrowhead}
        />
      );
    });
  });

  return <>{lines}</>;
}

interface RoundLabelsProps {
  layout: ReturnType<typeof calculateBracketLayout>;
}

function RoundLabels({ layout }: RoundLabelsProps) {
  // Group matches by round AND side (left/right/center)
  const roundInfo = new Map<string, { x: number; y: number; count: number }>();

  const centerX = layout.width / 2;

  layout.matches.forEach(pos => {
    const round = pos.match.round;
    const side = pos.x < centerX - 100 ? 'left' : pos.x > centerX + 100 ? 'right' : 'center';
    const key = `${round}-${side}`;

    const current = roundInfo.get(key);
    if (!current) {
      roundInfo.set(key, { x: pos.x, y: pos.y, count: 1 });
    } else {
      // For left side, use minimum X; for right side, use maximum X
      if (side === 'left') {
        current.x = Math.min(current.x, pos.x);
      } else if (side === 'right') {
        current.x = Math.max(current.x, pos.x);
      }
      // Track the Y position for 3rd place label positioning
      current.y = Math.min(current.y, pos.y);
      current.count++;
    }
  });

  const labels: ReactElement[] = [];

  roundInfo.forEach(({ x, y }, key) => {
    const [round] = key.split('-');

    // For 3rd place match, position label directly above the match
    const isThirdPlace = round === '3RD';
    const labelTop = isThirdPlace ? y + 50 - 25 : 20;

    labels.push(
      <div
        key={key}
        className="absolute text-xs font-bold text-gray-400 uppercase tracking-wider"
        style={{
          left: x + 50,
          top: labelTop,
          width: CARD_WIDTH,
          textAlign: 'center',
        }}
      >
        {getRoundLabel(round)}
      </div>
    );
  });

  return <>{labels}</>;
}
